"use client";

import NavigationArrow from "@/assets/NavigationArrow";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, { useCallback, useEffect, useRef } from "react";

const TWEEN_FACTOR_BASE = 0.1;

const numberWithinRange = (number: number, min: number, max: number) =>
  Math.min(Math.max(number, min), max);

interface EmblaCarouselProps {
  children: React.ReactNode[];
  options?: any;
  onSlideChange?: (index: number) => void;
  activeIndex?: number;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  initialIndex?: number;
}

const EmblaCarousel: React.FC<EmblaCarouselProps> = ({
  children,
  options,
  onSlideChange,
  left_navigation_icon,
  left_navigation_icon_alt,
  right_navigation_icon,
  right_navigation_icon_alt,
  initialIndex,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    slidesToScroll: 1,
    ...options,
  });
  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLDivElement[]>([]);

  const setTweenNodes = useCallback(() => {
    if (!emblaApi) return;
    tweenNodes.current = emblaApi
      .slideNodes()
      .map(
        (slideNode: HTMLElement) =>
          slideNode.querySelector(".embla__slide__card") as HTMLDivElement
      );
  }, [emblaApi]);

  const setTweenFactor = useCallback(() => {
    if (!emblaApi) return;
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, [emblaApi]);

  const tweenScale = useCallback(() => {
    if (!emblaApi) return;
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();

    emblaApi.scrollSnapList().forEach((snap: number, snapIndex: number) => {
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex: number) => {
        const slideNode = tweenNodes.current[slideIndex];
        if (!slideNode) return;

        let diffToTarget = snap - scrollProgress;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem: any) => {
            const target = loopItem.target();
            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);
              if (sign === -1) diffToTarget = snap - (1 + scrollProgress);
              if (sign === 1) diffToTarget = snap + (1 - scrollProgress);
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
        const isCenter = Math.abs(diffToTarget) < 0.01;
        const scale = isCenter ? 1 : 0.9;
        slideNode.style.transform = `scale(${scale})`;
        slideNode.style.transition =
          "transform 0.3s ease-out, opacity 0.3s ease-out";
        slideNode.style.zIndex = isCenter ? "100" : "90";
      });
    });
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setTweenNodes();
    setTweenFactor();
    tweenScale();

    const onSelect = () => {
      tweenScale();
      if (onSlideChange) {
        onSlideChange(emblaApi.selectedScrollSnap());
      }
    };

    emblaApi
      .on("reInit", () => {
        setTweenNodes();
        setTweenFactor();
        tweenScale();
      })
      .on("scroll", () => tweenScale())
      .on("select", onSelect);

    // Initialize with the specified index or default to middle index
    const targetIndex =
      initialIndex !== undefined
        ? initialIndex
        : Math.floor(children.length / 2);
    emblaApi.scrollTo(targetIndex, true);
    if (onSlideChange) {
      onSlideChange(targetIndex);
    }
  }, [
    emblaApi,
    setTweenNodes,
    setTweenFactor,
    tweenScale,
    onSlideChange,
    children.length,
    initialIndex,
  ]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <div className="w-full relative mx-auto">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex py-3 ">
          {React.Children.map(children, (child, index) => (
            <div
              className={cn(
                "embla__slide flex justify-center items-center",
                "flex-[0_0_76%]",
                "sm:flex-[0_0_20%]"
              )}
              key={index}
            >
              <div className="embla__slide__card w-full ">{child}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-3 ">
        <button
          onClick={scrollPrev}
          className="cursor-pointer transition-transform z-10"
          aria-label="Previous slide"
        >
          {left_navigation_icon ? (
            <div
              className={cn(
                "w-[36px] h-[36px]",
                "sm:w-[38px] sm:h-[38px]",
                "md:w-[42px] md:h-[42px]",
                "lg:w-[56px] lg:h-[56px]",
                emblaApi?.canScrollPrev() ? "opacity-100" : "opacity-30"
              )}
            >
              <Image
                src={left_navigation_icon}
                alt={left_navigation_icon_alt || "Previous"}
                width={100}
                height={100}
                className="cursor-pointer transition-transform w-full h-full object-contain"
              />
            </div>
          ) : (
            <NavigationArrow
              className="xs:size-9 sm:size-[38px] lg:size-[52px] rotate-180"
              fill="#f74958"
              pathFill="#F8F9FA"
            />
          )}
        </button>

        <button
          onClick={scrollNext}
          className="cursor-pointer transition-transform z-10"
          aria-label="Next slide"
        >
          {right_navigation_icon ? (
            <div
              className={cn(
                "w-[36px] h-[36px]",
                "sm:w-[38px] sm:h-[38px]",
                "md:w-[42px] md:h-[42px]",
                "lg:w-[56px] lg:h-[56px]",
                emblaApi?.canScrollNext() ? "opacity-100" : "opacity-30"
              )}
            >
              <Image
                src={right_navigation_icon}
                alt={right_navigation_icon_alt || "Next"}
                width={100}
                height={100}
                className="cursor-pointer transition-transform w-full h-full object-contain"
              />
            </div>
          ) : (
            <NavigationArrow
              className="xs:size-9 sm:size-[38px] lg:size-[52px]"
              fill="#f74958"
              pathFill="#F8F9FA"
            />
          )}
        </button>
      </div>
    </div>
  );
};

export default EmblaCarousel;
